import { DateRange } from "@mui/x-date-pickers-pro";
import { cloneDeep } from "lodash";
import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { GridData, GridRow, ValueFieldDef } from "../../../components/ReportGrid/ReportGrid.types";
import { AppContext } from "../../../core/context/appContextProvider";
import { getAllArkAccounts, getArkLedgers, getAttributes } from "../../../services/arkGL.service";
import { getFundsList } from "../../../services/fund.service";
import { getQuarterStartEndDateByDate } from "../../../utils/helpers/quarter.helper";
import { awaitReactUpdate } from "../../../utils/helpers/timeoutFunctions";
import { useEffectAsync } from "../../../utils/hooks/useEffectAsync.hook";
import { AccountCategoryProps, FSDisplayCategoryProps } from "../../../utils/types/arkGLFinancialStatementsTypes";
import { FilterItem } from "../../../utils/types/listItems";
import { ViewTypes } from "../../fund/scheduleOfInvestments/ScheduleOfInvestments.hooks";
import { CreditRange, DateColumnInfo, DebitRange, FSDisplayNotSet, GridCallerData, netIncomeTotalCatStr, netInvestmentIncomeCatStr, netRealizedCatStr, netUnrealizedCatStr, ReportDetailsParams, retainedEarningsTotalCatStr, retainedInvestmentIncomeCatStr, retainedRealizedCatStr, retainedUnrealizedCatStr } from "../reports/glReports/shared";

const financialStatementsList = [
    { id: "balance_sheet_fs_mapping", label: "Balance Sheet" },
    { id: "income_statement_fs_mapping", label: "Income Statement" },
    { id: "Unrealized_SOI", label: "Unrealized SOI" },
    { id: "Realized_SOI", label: "Realized SOI" },
    { id: 'statement_of_changes_in_partners', label: 'Statement of Changes in Partners' },
    { id: 'statement_of_cash_flows', label: 'Statement of Cash Flows' }
];

export enum FsViews {
    balanceSheet = 'balance_sheet_fs_mapping',
    incomeStatement = 'income_statement_fs_mapping',
    unrealizedSOI = 'Unrealized_SOI',
    realizedSOI = 'Realized_SOI',
    statementOfChangesInPartners = 'statement_of_changes_in_partners',
    statementOfCashFlows = 'statement_of_cash_flows'
  };

const defaultStartEndDates = getQuarterStartEndDateByDate(new Date());
const defaultEndDateToday = new Date();
const currentYear = new Date().getFullYear();
const incomeStateStartDate: Date = new Date(`${currentYear}-01-01`);
const incomeDefaultDate: DateRange<Date> = 
  [new Date(incomeStateStartDate.getUTCFullYear(), incomeStateStartDate.getUTCMonth(), incomeStateStartDate.getUTCDate()),
   defaultEndDateToday];

type Account = {
    id: string,
    name: string,
    number: string,
    parentId: string,
    fundId: string,
    attributeId: string,
    fsDisplayName: string
};

type ReportAccount = {
    accountId: string,
    accountName: string,
    accountNo: string,
    fundId: string,
    attributeId: string,
    fsMappingId: string,
    fsName: string,
    state: string,
    startBalance: number,
    endBalance: number,
    totalAmount: number,
    lineItems: ReportLineItem[],
    parentAccountId: string,
    childAccounts: ReportAccount[],
    decimals: number,
    categoryProps?: AccountCategoryProps | FSDisplayCategoryProps
};

type ReportLineItem = {
    amount: number,
    date: Date,
    ledgerId: string,
    ledgerName: string,
    journalEntryNum: number,
    adjustingJournalEntry: boolean,
    memo: string,
    type?: string,
    state?: string,
    subtract?: boolean,
};

enum GlStatuses {
    DRAFT = 'Draft',
    POSTED = 'Posted'
};

export type TrialBalanceDetailsParams = {
  startDate: Date, 
  endDate: Date, 
  accounts: string[],
  ledgerIDs: string[], 
  glStatus: GlStatuses[],
  attributes: string[],
  fsName?: string,
  summaryRow?: boolean,
  isCustom?: boolean,
}

export const useFinancialStatements = (
    setCurrSOIview: Dispatch<SetStateAction<string>>,
    handleSoiViewChange: Function,
    ) => {
    const { state } = useContext(AppContext);
    const { fundId } = useParams<{ fundId: string }>();
    const clientId = state.loginUser.clientId;

    const [accounts, setAccounts] = useState<Account[]>([]);
    const [attributes, setAttributes] = useState<any[]>([]);
    const [attributeFilterDisabled, setAttributeFilterDisabled] = useState(false);
    const [currentCurrency, setCurrentCurrency] = useState('');
    const [dateRange, setDateRange] = useState<DateRange<Date>>([defaultStartEndDates.startDate, defaultEndDateToday]);
    const [filteredAccounts, setFilteredAccounts] = useState<FilterItem[]>([]);
    const [filteredAttributes, setFilteredAttributes] = useState<FilterItem[]>([]);
    const [filteredLedgers, setFilteredLedgers] = useState<FilterItem[]>([]);
    const [financialStatementsView, setFinancialStatementsView] = useState('');
    const [financialStatementsParams, setFinancialStatementsParam] = useState<TrialBalanceDetailsParams>();
    const [fsDetailsApiParams, setFSDetailsApiParams] = useState<ReportDetailsParams>();
    const [funds, setFunds] = useState<any[]>([]);
    const [fundsFilter, setFundFilter] = useState<FilterItem[]>([]);
    const [gridData, setGridData] = useState<GridData>();
    const [hideAllZeros, setHideAllZeros] = useState(false);
    const [invalidSelectedLedgers, setInvalidSelectedledgers] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [ledgers, setLedgers] = useState<any[]>([]);
    const [level1Clicked, setLevel1Clicked] = useState<boolean>(false);
    const [mainFSListData, setMainFSListData] = useState<GridData>();
    const [netRow, setNetRow] = useState(false);
    const [retainedRow, setRetainedRow] = useState(false);
    const [runFinancialStatementsView, setRunFinancialStatementsView] = useState("");
    const [statusFilter, setStatusFilter] = useState<FilterItem[]>([]);
    const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
    const [selectedAttributes, setSelectedAttributes] = useState<string[]>([]);
    const [selectedFunds, setSelectedFunds] = useState<string[]>([]);
    const [selectedLedgers, setSelectedLedgers] = useState<string[]>([]);
    const [selectedStatus, setSelectedStatus] = useState<GlStatuses[]>([]);
    const [soiView, setSOIView] = useState<boolean>(false);
    const [summaryRowClicked, setSummaryRowClicked] = useState<boolean>(false);
    const [suppressIfZero, setSuppressIfZero] = useState(true);
    const [tempAttribsFilterResponse, setTempAttribsFilterResponse] = useState<FilterItem[]>([]);
    const [viewFiltersState, setViewFiltersState] = useState<any>({});
    const [zeroSumStartBalance, setZeroSumStartBalance] = useState<boolean>(false);


    useEffectAsync(async (isCanceled) => {
        setIsLoading(true);
        const allResponses = await getFilterData(isCanceled);
  
        if(isCanceled() || !allResponses) return;
  
        const { tempLedgers, tempAccounts, tempAttribsFilter } = allResponses;
        const tempStatuses = [{ id: 'DRAFT', name: GlStatuses.DRAFT }, { id: 'POSTED', name: GlStatuses.POSTED }] as FilterItem[];
        const selectedStatusIDs = tempStatuses.map((item: any) => item.id);
  
        setSelectedAttributes(tempAttribsFilter.map((item: any) =>  item.id));
        setFilteredAttributes(tempAttribsFilter);
        setTempAttribsFilterResponse(tempAttribsFilter);
        setStatusFilter(tempStatuses);
        setSelectedStatus(selectedStatusIDs);
        setFinancialStatementsView(FsViews.balanceSheet);
  
        if(fundId){
          setSelectedFunds([fundId]);
          setLedgerAndAccountFilters([fundId], tempLedgers, tempAttribsFilter.map((item: any) =>  item.id), [], tempAccounts, []);
        }

        setIsLoading(false);
      }, []);

    let viewFilters: {
        selectedAttributes: any[],
        filteredAttributesFilter: FilterItem[],
        rollupMode: boolean,
        hideUseerCategorySelector: boolean
    };

    useEffectAsync(async (isCanceled) => {
        setIsLoading(true);

        switch(financialStatementsView){
          case FsViews.balanceSheet:
            setDateRange([ null , defaultEndDateToday ]);
            viewFilters = buildBalanceSheetFilters();
            setHideAllZeros(true);
            setAttributeFilterDisabled(true);
            setSelectedAttributes(viewFilters.selectedAttributes);
            setLedgerAndAccountFilters(selectedFunds, ledgers, viewFilters.selectedAttributes, selectedLedgers, accounts, []);
            setViewFiltersState(viewFilters);
            break;
          case FsViews.incomeStatement:
            setDateRange(incomeDefaultDate);
            viewFilters = buildIncomeStatementFilters();
            setHideAllZeros(true);
            setAttributeFilterDisabled(true);
            setSelectedAttributes(viewFilters.selectedAttributes);
            setLedgerAndAccountFilters(selectedFunds, ledgers, viewFilters.selectedAttributes, selectedLedgers, accounts, []);
            setViewFiltersState(viewFilters);
            break;
          case FsViews.statementOfChangesInPartners:
            setAttributeFilterDisabled(false);
            setViewFiltersState({});
            break;
          case FsViews.statementOfCashFlows:
            setAttributeFilterDisabled(false);
            setViewFiltersState({});
            break;
          case FsViews.realizedSOI:
            setCurrSOIview(financialStatementsView);
            handleSoiViewChange(financialStatementsView);
            setViewFiltersState({});
            break;
          case FsViews.unrealizedSOI:
            setCurrSOIview(financialStatementsView);
            handleSoiViewChange(financialStatementsView);
            setViewFiltersState({});
            break;
          }

          await awaitReactUpdate();
          runFinancialStatementsView && validFilters ? setRunFinancialStatementsView(financialStatementsView) : setRunFinancialStatementsView("");
    
          soiView ? setRunFinancialStatementsView(financialStatementsView) : '';

        !soiView && validFilters ? '' : setIsLoading(false);
        !soiView && !validFilters ? setIsLoading(false) : '';
    }, [financialStatementsView]);

    useEffect(() => {

      const fsParams: TrialBalanceDetailsParams = {
        startDate: dateRange[0]!,
        endDate: dateRange[1]!,
        accounts: selectedAccounts,
        glStatus: selectedStatus,
        ledgerIDs: selectedLedgers,
        attributes: selectedAttributes,
      };

      setFinancialStatementsParam(fsParams);

    }, [selectedAccounts, selectedAttributes, selectedFunds, selectedLedgers, dateRange]);

    function buildBalanceSheetFilters() {
      const defaultAttribFilters = ['Assets', 'Income', 'Expense', 'Gain/Loss', 'Liabilities', 'Partners Capital'];
      
      const tempFilteredAttribs =  tempAttribsFilterResponse.filter(a => defaultAttribFilters.find(df => a.name===df));
      const attributeIds = tempFilteredAttribs.map(item =>  item.id);
      
      return {
        selectedAttributes: attributeIds,
        filteredAttributesFilter: tempFilteredAttribs,
        rollupMode: false,
        hideUseerCategorySelector: true
      };
    };

    function buildIncomeStatementFilters() {
      const defaultAttribFilters = ['Expense', 'Gain/Loss', 'Income'];
      const tempFilteredAttribs =  tempAttribsFilterResponse.filter(a => defaultAttribFilters.find(df => a.name===df));
      const attributeIds = tempFilteredAttribs.map(item =>  item.id);

      return {
        selectedAttributes: attributeIds,
        filteredAttributesFilter: tempFilteredAttribs,
        rollupMode: false,
        hideUseerCategorySelector: true
      };
    };
    
    async function getFilterData(isCanceled: () => boolean) {
        let tempFunds: any;
        let tempFundFilters: FilterItem[];
        let tempLedgers: any[];
        let tempAccounts: Account[];
        let attribsResponse: any[];
  
        if(!funds?.length || !ledgers?.length || !accounts?.length || !attributes?.length) {
          const allResponses = await Promise.all([
            getFundsList(clientId),
            getAttributes(),
            getAllArkAccounts(),
            getArkLedgers()
          ]);
  
          if(isCanceled()) return;
  
          const fundsApiResponse = allResponses[0];
          const attributesApiResponse = allResponses[1];
          const accountsApiResponse = allResponses[2];
          const legdersApiResponse = allResponses[3];
  
          // *********  funds **************
          tempFunds = fundsApiResponse;
  
          tempFundFilters = tempFunds.sort((a:any,b:any) => a.fund.name < b.fund.name ? -1: 1).map((item:any) => { 
            return { id: item.fund.id, name: item.fund.name } as FilterItem; 
          }) as FilterItem[];
  
          if(fundId){
            const fundFilter: any = tempFundFilters.find(fund => fund.id === fundId);
            const tempFund = tempFunds.find((fund: any) => fund.fund.id === fundId);
  
            setFunds([tempFund]);
            setFundFilter([fundFilter]);
          } else {
            setFunds(tempFunds);
            setFundFilter(tempFundFilters);
          }
    
          // *********  ledgers **************
          tempLedgers = (legdersApiResponse.items as any[]).sort((a,b) => a.name < b.name ? -1 : 1);
    
          setLedgers(tempLedgers);
  
          // *********  accounts **************
          tempAccounts = accountsApiResponse.items as Account[];
          tempAccounts.filter(x => !x.fsDisplayName).forEach(x => x.fsDisplayName = FSDisplayNotSet);
          tempAccounts = tempAccounts.sort((a,b) => a.name < b.name ? -1 : 1);
    
          setAccounts(tempAccounts);
  
          // *********  attibutes **************
          attribsResponse = attributesApiResponse.items;
          setAttributes(attribsResponse);
        } else {
          tempFunds = funds;
          tempFundFilters = fundsFilter;
          tempLedgers = ledgers;
          tempAccounts = accounts;
          attribsResponse = attributes;
        }
  
        const tempAttribsFilter = attribsResponse.sort((a,b) => a.type < b.type ? -1 : 1).map((item:any) => {
          return { id: item.id, name: item.type } as FilterItem; 
        });
  
      
        return {
          tempFunds,
          tempFundFilters,
          tempLedgers,
          tempAccounts,
          tempAttribsFilter
        };
    };

    
    function handleAccountFilterChange(_filter: any, accountIds: string[]) {
        setRunFinancialStatementsView("");
        setSelectedAccounts(accountIds);
        // setGridData(undefined);
    };

    function handleAttributeFilterChange(_filter: any, attributeIds: string[]) {
        setRunFinancialStatementsView("");
        setSelectedAttributes(attributeIds);
        setLedgerAndAccountFilters(selectedFunds, ledgers, attributeIds, selectedLedgers, accounts, []);
        // setGridData(undefined);
    };

    function handleStatusFilterChange(_filter: any, statusIds: GlStatuses[]) {
        setRunFinancialStatementsView("");
        setSelectedStatus(statusIds);
        // setGridData(undefined);
    };

    function handleDateRangeChange(dateRange: DateRange<Date>, clearGrid = true) {
        if(clearGrid) {
            setRunFinancialStatementsView("");
            //   setGridData(undefined);
        //   setDataFieldOrder([]);
        }
        
        setDateRange(dateRange);
    };


    function handleLedgerFilterChange(_filter: any, ledgerIDs: string[]) {
        setRunFinancialStatementsView("");
        let invalid = false;
    
        if(ledgerIDs.length) {
          const firstSelLedger = ledgers.find(l => l.id === ledgerIDs[0]);
    
          ledgerIDs.every(lId => {
            const ledger = ledgers.find(l => l.id === lId);
    
            if(firstSelLedger.currency === ledger.currency) {
              return true;
            } else {
              invalid = true;
              return false;
            }
          });
        }

        const firstSelLedgerId = ledgerIDs?.length ? ledgerIDs[0] : undefined;
        const gl = ledgers.find(l => l.id === firstSelLedgerId);
    
        setCurrentCurrency(gl?.currency??'');
        setInvalidSelectedledgers(invalid);
        setSelectedLedgers(ledgerIDs);
        // setGridData(undefined);
    };

    function setLedgerAndAccountFilters(
        selectedFunds: string[], 
        ledgers: any[], 
        selectedAttributes: string[], 
        selectedLedgers: string[],  
        accounts: Account[], 
        selectedAccounts: string[]) {
        
        const newLedgerFilter = ledgers.filter(item => selectedFunds.includes(item.fundId));
        const tempLedgers = newLedgerFilter.sort((a,b) => a.currency < b.currency ? -1 : 1).map(item => {
          return { id: item.id, name: `${item.name} (${item.currency})` } as FilterItem; 
        });

        setFilteredLedgers(tempLedgers);
        setSelectedLedgers(selectedLedgers);
    
        const newAccountFilter1 = accounts.filter(item => selectedFunds.includes(item.fundId));

        const newAccountFilter2 = newAccountFilter1.filter(item => selectedAttributes.includes(item.attributeId));
    
        const tempFilteredAccounts = newAccountFilter2.map(item => {
          return { id: item.id, name: item.name } as FilterItem;
        });
    
        const newSelAcctIDs = tempFilteredAccounts.map(item => item.id);
    
        setFilteredAccounts(tempFilteredAccounts);
        setSelectedAccounts(newSelAcctIDs);
        // setGridData(undefined);
    };

    function handleFundFilterChange(_filter: any, fundIDs: string[]) {
        setRunFinancialStatementsView("");
        setSelectedFunds(fundIDs);
        setSelectedLedgers([]);
        setLedgerAndAccountFilters(fundIDs, ledgers, selectedAttributes, [], accounts, []);
    };

    function suppressGridRowsIfZero(reportAccounts: ReportAccount[], checkForMultiChildren: boolean) {
        let acctWitNoChildrenIdx: number;
      
        do {
          if(!checkForMultiChildren) {
            acctWitNoChildrenIdx = reportAccounts.findIndex(acct => !acct.lineItems?.length);
          } else {
            acctWitNoChildrenIdx = reportAccounts.findIndex(acct => !acct.lineItems?.length && !acct.childAccounts?.length);
          }
          
          if(acctWitNoChildrenIdx >= 0) {        
            const acctWitNoChildren = reportAccounts[acctWitNoChildrenIdx];
      
            const parentAcct = reportAccounts.find(acct => acct.accountId === acctWitNoChildren!.parentAccountId);
      
            if(parentAcct) {
              const childIdx = parentAcct.childAccounts.findIndex(ca => ca.accountId === acctWitNoChildren!.accountId);
      
              parentAcct.childAccounts.splice(childIdx, 1);
            }
      
            reportAccounts.splice(acctWitNoChildrenIdx, 1);
          }
        } while(acctWitNoChildrenIdx >= 0);
    };


    function handleSuppressIfZeroChange(_filter: any, suppressIfZero: boolean) {
        setRunFinancialStatementsView("");
        setSuppressIfZero(suppressIfZero);
        // if(gridData) {
        //   handleRunReportButtonClick();
        // }
    };

    function handleViewSelection (evt: any) {
        if(evt.target.value === FsViews.unrealizedSOI || evt.target.value === FsViews.realizedSOI){
            setSOIView(true);
            setFinancialStatementsView(evt.target.value);
        } else {
            setSOIView(false);
            setFinancialStatementsView(evt.target.value);
        }
    };

    function getNoDataMessage() {
        if(invalidSelectedLedgers) {
          return 'GL Name filters must be of the same currency.';
        } else if(!validFilters) {
          return 'Select filters above, then click Apply button to run the financial statement.';
        } else if (!isLoading && !runFinancialStatementsView) {
          return 'Click Apply to run the financial statement.';
        } else if (isLoading) {
          return '';
        } else {
          return undefined;
        }
    };

    function areValidFilters() {
        return (
          !!selectedFunds.length && 
          !!selectedLedgers.length && 
          !!selectedAccounts.length && 
          !invalidSelectedLedgers
        );
    };

    const validFilters = areValidFilters();
    const noDataMessage = getNoDataMessage();

    const handleRunFinancialStatementsButtonClick = () => {
        setRunFinancialStatementsView(financialStatementsView);
    };

    function handleValueLinkButtonClick(
      gridRow: GridRow,
      valueFieldOrder: ValueFieldDef,
      dateColInfo: DateColumnInfo[],
      isCustom: boolean,
      currentDecimals: number,
      netCatStartDate?: Date,
      retainedCatEndDate?: Date) {

      const dateColumn: any = cloneDeep(dateColInfo.find(c => c.name === valueFieldOrder.name)!);

      const callerDateArray = gridRow.callerDataArray.filter(cd => !!cd) as GridCallerData[];
      const accountIds: string[] = [];
      let summaryRow: boolean = false;
  
      // Pass fsName into params for correctly parsing the returned data from the second API call for details
      const fsName = gridRow.callerDataArray[0].reportAccounts[0].fsName;
  
      // Pass summaryRow into params for currectly including parent row data in totals click through details
      if(gridRow.entityId.includes("Summary")){
        summaryRow = true;
      }
      
      valueFieldOrder.category === "Yearly"
      || valueFieldOrder.category === "Quarterly"
      || valueFieldOrder.category === "Months"
      || valueFieldOrder.name === CreditRange
      || valueFieldOrder.name === DebitRange
      || valueFieldOrder.name.includes("selected")
      ? setZeroSumStartBalance(true) : setZeroSumStartBalance(false);
  
      // Prop drilled setLevel1Clicked to indicate if a custom view top parent details view is clicked to include all parent values
      if(gridRow.level === 1 && isCustom) {
        setLevel1Clicked(true);
      } else {
        setLevel1Clicked(false);
      }
  
      // Now that summaryRow is passed as a param this prop drilled is believed to be obsolete
      gridRow.entityId.includes("Summary") ? setSummaryRowClicked(true) : setSummaryRowClicked(false);
      
      callerDateArray.forEach(cd => {
        cd.reportAccounts.forEach(ra => {
          accountIds.push(ra.accountId);
        });
      });

      if(
        gridRow.categoryName === netInvestmentIncomeCatStr
        || gridRow.categoryName === netRealizedCatStr
        || gridRow.categoryName === netUnrealizedCatStr
        || gridRow.categoryName === netIncomeTotalCatStr){
          if(
            dateColumn.name.includes('monthly') ||
            dateColumn.name.includes('quarterly') ||
            dateColumn.name.includes('yearly')){
              '';
          } else {
          dateColumn.startDate = netCatStartDate;
          dateColumn.endDate = dateRange[1];
          }
          setNetRow(true);
        } else if(
          gridRow.categoryName === retainedInvestmentIncomeCatStr
          || gridRow.categoryName === retainedRealizedCatStr
          || gridRow.categoryName === retainedUnrealizedCatStr
          || gridRow.categoryName === retainedEarningsTotalCatStr){
            if(
              dateColumn.name.includes('monthly') ||
              dateColumn.name.includes('quarterly') ||
              dateColumn.name.includes('yearly') ){
                '';
            } else {
            dateColumn.endDate = retainedCatEndDate;
            dateColumn.startDate = dateRange[0];
            }
            setRetainedRow(true);
        } else {
          setNetRow(false);
          setRetainedRow(false);
        }

      const params:ReportDetailsParams = {
        fundIds: selectedFunds,
        ledgerIds: selectedLedgers,
        accountIds: accountIds,
        attributeIds: selectedAttributes,
        journalEntryStates: selectedStatus,
        dateColInfos: dateColumn,
        currency: currentCurrency,
        decimals: currentDecimals,
        fsName: fsName,
        summaryRow: summaryRow,
        isCustom: isCustom,
        level1Clicked: gridRow.level === 1 && isCustom ? true : false
      };
  
      setFSDetailsApiParams(params);    
    }

    function handleDataGridChange(gridData: GridData, exportName: string) {
      setGridData(gridData);
      // setExportName(exportName);
    }

    function handleCloseReportDetails() {
      setFSDetailsApiParams(undefined);
      setZeroSumStartBalance(false);
      setSummaryRowClicked(false);
      // setCustomEndBalanceClicked(false);
    }

    return {
        attributeFilterDisabled,
        attributes,
        dateRange,
        filteredAccounts,
        filteredAttributes,
        filteredLedgers,
        financialStatementsList,
        financialStatementsParams,
        financialStatementsView,
        fsDetailsApiParams,
        funds,
        fundsFilter,
        handleAccountFilterChange,
        handleAttributeFilterChange,
        handleCloseReportDetails,
        handleDataGridChange,
        handleDateRangeChange,
        handleFundFilterChange,
        handleLedgerFilterChange,
        handleRunFinancialStatementsButtonClick,
        handleStatusFilterChange,
        handleSuppressIfZeroChange,
        handleValueLinkButtonClick,
        handleViewSelection,
        invalidSelectedLedgers,
        isLoading,
        level1Clicked,
        netRow,
        noDataMessage,
        retainedRow,
        runFinancialStatementsView,
        selectedAccounts,
        selectedAttributes,
        selectedFunds,
        selectedLedgers,
        selectedStatus,
        setIsLoading,
        setMainFSListData,
        soiView,
        statusFilter,
        summaryRowClicked,
        suppressIfZero,
        validFilters,
        viewFiltersState,
        zeroSumStartBalance
    };
};